import { useEffect, useState } from 'react';
import type { MiDia, Tarea } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { fechaLarga } from '../componentes.js';

/**
 * Tareas del día: lo que a esta persona le toca hoy.
 *
 * Junta dos cosas que hasta ahora vivían separadas —las tareas de proyecto de
 * las que es responsable y las visitas que tiene agendadas— porque para quien
 * empieza la jornada son una sola pregunta. Un manager que tiene que mirar en
 * dos sitios acaba mirando en uno.
 *
 * Lo atrasado aparece aquí, marcado. Una tarea que vencía ayer y sigue abierta
 * no deja de ser cosa de hoy porque haya cambiado la fecha del calendario.
 */

const hoy = () => new Date().toISOString().slice(0, 10);

export function TareasDelDia({
  idioma,
  onAbrirCalendario,
  onAbrirProyecto,
  onAbrirVisita,
  onAbrirIncidencia,
  onAbrirOportunidad,
}: {
  idioma: Idioma;
  onAbrirCalendario?: () => void;
  /** Al tocar una tarea de proyecto se va al proyecto, no al calendario. */
  onAbrirProyecto?: (proyectoId: string) => void;
  /** Al tocar una visita se va a la ficha de su casa, a la pestana de visitas. */
  onAbrirVisita?: (propiedadId: string) => void;
  /** Al tocar una incidencia vencida se va a la incidencia. */
  onAbrirIncidencia?: (incidenciaId: string, propiedadId: string) => void;
  /** Al tocar una tarea del CRM se abre la ficha de su oportunidad. */
  onAbrirOportunidad?: (oportunidadId: string) => void;
}) {
  const [dia, setDia] = useState<MiDia | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = () =>
    api
      .proyMiDia()
      .then((r) => setDia(r.dia))
      .catch((e: unknown) =>
        setError(e instanceof ErrorApi ? e.message : t(idioma, 'dia.noSeCargo')),
      );

  useEffect(() => {
    void cargar();
  }, []);

  if (error) {
    return (
      <section className="tarjeta">
        <h2>{t(idioma, 'dia.titulo')}</h2>
        <p className="error" role="alert">
          {error}
        </p>
      </section>
    );
  }
  if (!dia) {
    return (
      <section className="tarjeta">
        <h2>{t(idioma, 'dia.titulo')}</h2>
        <p className="suave">{t(idioma, 'app.cargando')}</p>
      </section>
    );
  }

  const dias = hoy();
  const pendientes =
    dia.tareas.length + dia.visitas.length + dia.incidencias.length + dia.crm.length;

  const completarCrm = (id: string) => {
    setError(null);
    api
      .crmCompletarActividad(id, true)
      .then(cargar)
      .catch((e: unknown) =>
        setError(e instanceof ErrorApi ? e.message : t(idioma, 'dia.noSeCompleto')),
      );
  };

  const completar = (tarea: Tarea) => {
    setError(null);
    api
      .proyEditarTarea(tarea.id, { estado: 'HECHA' })
      // Al completarla deja de tocar hoy, así que se vuelve a pedir el día en
      // lugar de tacharla en pantalla y dejarla ocupando sitio.
      .then(cargar)
      .catch((e: unknown) =>
        setError(e instanceof ErrorApi ? e.message : t(idioma, 'dia.noSeCompleto')),
      );
  };

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{t(idioma, 'dia.titulo')}</h2>
        <span className="suave pequeno numerico">
          {pendientes === 0
            ? t(idioma, 'dia.nadaPendiente')
            : t(idioma, 'dia.pendientes', { total: pendientes })}
        </span>
      </div>

      {pendientes === 0 && (
        <p className="suave">{t(idioma, 'dia.vacio')}</p>
      )}

      {/*
        LAS QUE YA DEBERIAN ESTAR CERRADAS, PRIMERO.

        Una incidencia con fecha prometida que ha pasado no es un aviso de
        fondo: es lo primero que hay que mirar al empezar el dia, antes que la
        visita de las once. Por eso va delante.
      */}
      {dia.incidencias.length > 0 && (
        <>
          <h3 className="ajuste-grupo">{t(idioma, 'dia.incidencias')}</h3>
          <ul className="lista-dia">
            {dia.incidencias.map((inc) => (
              <li key={inc.id}>
                <span className={`chip-nivel N${inc.nivel}`}>N{inc.nivel}</span>
                <span className="crecer">
                  {onAbrirIncidencia ? (
                    <button
                      type="button"
                      className="enlace tarea-titulo"
                      onClick={() => onAbrirIncidencia(inc.id, inc.propiedadId)}
                    >
                      {inc.titulo}
                    </button>
                  ) : (
                    <b>{inc.titulo}</b>
                  )}
                  <small>
                    {inc.propiedadNombre ?? inc.codigo}
                    {inc.fechaEsperada ? ` · ${fechaLarga(inc.fechaEsperada, idioma)}` : ''}
                  </small>
                </span>
                {/*
                  Atrasada y hoy mandan sobre «asignada»: que sea tuya no quita que
                  vaya tarde. Una tuya sin fecha, o con fecha que aun no llega, dice
                  simplemente que es tuya.
                */}
                {inc.fechaEsperada && inc.fechaEsperada < dias ? (
                  <span className="pastilla rojo">{t(idioma, 'dia.atrasada')}</span>
                ) : inc.fechaEsperada === dias ? (
                  <span className="pastilla ambar">{t(idioma, 'dia.hoy')}</span>
                ) : (
                  <span className="pastilla neutra">{t(idioma, 'dia.asignada')}</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {dia.visitas.length > 0 && (
        <>
          <h3 className="ajuste-grupo">{t(idioma, 'dia.visitas')}</h3>
          <ul className="lista-dia">
            {dia.visitas.map((v) => {
              /*
                Tocar la visita lleva a SU CASA, a la pestana de visitas: es
                donde se inicia, se programa o se quita de la agenda. Antes
                llevaba al calendario del mes, que dice cuando pero no deja
                hacer nada con ella. Si la visita no cuelga de ninguna casa
                —las hay— queda el calendario, que es lo unico que aplica.
              */
              const irAsuCasa = v.propiedadId && onAbrirVisita
                ? () => onAbrirVisita(v.propiedadId!)
                : onAbrirCalendario;
              return (
                <li key={v.id}>
                  <span className="hora">{v.hora ?? '—'}</span>
                  <span className="crecer">
                    <b>{v.titulo}</b>
                    <small>
                      {v.propiedadNombre ?? t(idioma, 'dia.sinPropiedad')} ·{' '}
                      {t(idioma, `evento.${v.tipo}`)}
                    </small>
                  </span>
                  {irAsuCasa && (
                    <button type="button" className="enlace" onClick={irAsuCasa}>
                      {t(idioma, 'dia.ver')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/*
        LAS DEL CRM, CON SU CLIENTE. «Llamar a Christian» sin decir de qué
        oportunidad es obliga a abrirla para saber de qué va; con el nombre y el
        embudo al lado, se sabe antes de tocar.
      */}
      {dia.crm.length > 0 && (
        <>
          <h3 className="ajuste-grupo">{t(idioma, 'dia.crm')}</h3>
          <ul className="lista-dia">
            {dia.crm.map((tarea) => (
              <li key={tarea.id}>
                <button
                  type="button"
                  className="circulo"
                  aria-label={t(idioma, 'dia.completarTarea', { titulo: tarea.titulo })}
                  onClick={() => completarCrm(tarea.id)}
                />
                <span className="crecer">
                  {onAbrirOportunidad ? (
                    <button
                      type="button"
                      className="enlace tarea-titulo"
                      onClick={() => onAbrirOportunidad(tarea.oportunidadId)}
                    >
                      {tarea.titulo}
                    </button>
                  ) : (
                    <b>{tarea.titulo}</b>
                  )}
                  <small>
                    {tarea.oportunidadNombre} · {tarea.embudoNombre}
                    {tarea.vence !== null && ` · ${fechaLarga(tarea.vence, idioma)}`}
                  </small>
                </span>
                {tarea.vence !== null && tarea.vence < dias ? (
                  <span className="pastilla rojo">{t(idioma, 'dia.atrasada')}</span>
                ) : tarea.vence === dias ? (
                  <span className="pastilla ambar">{t(idioma, 'dia.hoy')}</span>
                ) : tarea.vence === null ? (
                  <span className="pastilla neutra">{t(idioma, 'dia.asignada')}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}

      {dia.tareas.length > 0 && (
        <>
          <h3 className="ajuste-grupo">{t(idioma, 'dia.deProyectos')}</h3>
          <ul className="lista-dia">
            {dia.tareas.map((tarea) => {
              const atrasada = tarea.vence !== null && tarea.vence < dias;
              const esHoy = tarea.vence === dias;
              return (
                <li key={tarea.id}>
                  <button
                    type="button"
                    className="circulo"
                    aria-label={t(idioma, 'dia.completarTarea', { titulo: tarea.titulo })}
                    onClick={() => completar(tarea)}
                  />
                  {/*
                    El titulo abre el proyecto. Una tarea sin su proyecto delante
                    no se puede resolver: falta la conversacion, los adjuntos y
                    las demas tareas que van con ella.
                  */}
                  <span className="crecer">
                    {onAbrirProyecto ? (
                      <button
                        type="button"
                        className="enlace tarea-titulo"
                        onClick={() => onAbrirProyecto(tarea.projectId)}
                      >
                        {tarea.titulo}
                      </button>
                    ) : (
                      <b>{tarea.titulo}</b>
                    )}
                    <small>
                      {tarea.proyectoNombre}
                      {/* La fecha, escrita. «Atrasada» dice que se paso, no cuando. */}
                      {tarea.vence !== null && ` · ${fechaLarga(tarea.vence, idioma)}`}
                    </small>
                  </span>
                  {atrasada ? (
                    <span className="pastilla rojo">{t(idioma, 'dia.atrasada')}</span>
                  ) : esHoy ? (
                    <span className="pastilla ambar">{t(idioma, 'dia.hoy')}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
